import React, { useEffect, useState } from "react";
import dayjs from "dayjs";

import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogTitle from "@mui/material/DialogTitle";
import FormControl from "@mui/material/FormControl";
import InputAdornment from "@mui/material/InputAdornment";
import InputLabel from "@mui/material/InputLabel";
import MenuItem from "@mui/material/MenuItem";
import Select from "@mui/material/Select";
import TextField from "@mui/material/TextField";

import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { DateTimePicker } from "@mui/x-date-pickers/DateTimePicker";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";

import { ConvertFeetToMeters, ConvertMetersToFeet, DateHasAlreadyPassed, getConstraintDurationFromInput, GetTimezone, YEAR_IN_MINUTES } from "../../util";

import { useUserAuth } from "../../contexts/authContext";

export const ConstraintEdit = (props) => {
    const ADVISORY_TYPE = "ADVISORY";
    const TRAINING_TYPE = "TRAINING";
    const COMMERCIAL_TYPE = "COMMERCIAL";
    const PUBLIC_SAFETY_TYPE = "PUBLIC_SAFETY";
    const EMERGENCY_TYPE = "EMERGENCY";
    const RESTRICTED_TYPE = "RESTRICTED";

    const [editName, setEditName] = useState("");
    const [editEnd, setEditEnd] = useState(dayjs());
    const [editStart, setEditStart] = useState(dayjs());
    const [editAltitude, setEditAltitude] = useState(0);
    const [editDuration, setEditDuration] = useState(0);
    const [editPriority, setEditPriority] = useState(0);
    const [editType, setEditType] = useState(ADVISORY_TYPE);
    const [publishedConstraint, setPublishedConstraint] = useState(false);

    const { handleFailedFetch } = useUserAuth();

    useEffect(() => {
        const startTime = new Date(props.constraintEditEntity.constraint.time_start).getTime();
        const endTime = new Date(props.constraintEditEntity.constraint.time_end).getTime();

        setEditName(props.constraintEditEntity.constraint.name);
        setEditType(props.constraintEditEntity.constraint.type);
        setEditStart(startTime);
        setEditEnd(endTime);
        setEditDuration(Math.floor((endTime - startTime) / 1000 / 60));
        setEditAltitude(ConvertMetersToFeet(props.constraintEditEntity.constraint.volumes[0].altitude_max_agl_m));
        setPublishedConstraint(props.constraintEditEntity.constraint.state === "ACCEPTED");
    }, []);

    // Fires if user hits save
    const handleUpdateConstraint = () => {
        if (!editName) {
            props.setSnackbar({ children: "Please enter a valid name", severity: "error" });
            return false;
        }
        if (!publishedConstraint && DateHasAlreadyPassed(editStart)) {
            props.setSnackbar({ children: "Start date has already passed", severity: "error" });
            return false;
        }
        if (isNaN(parseFloat(editAltitude)) || parseFloat(editAltitude) <= 0) {
            props.setSnackbar({ children: "Please enter a valid altitude", severity: "error" });
            return false;
        }
        if (isNaN(parseFloat(editDuration)) || parseFloat(editDuration) <= 0 || parseFloat(editDuration) >= YEAR_IN_MINUTES) {
            props.setSnackbar({ children: "Please enter a valid duration", severity: "error" });
            return false;
        }
        const constraint = props.constraintEditEntity.constraint;
        constraint.name = editName;
        constraint.time_end = editEnd;
        constraint.time_start = editStart;
        constraint.altitude = ConvertFeetToMeters(parseFloat(editAltitude));
        constraint.version = parseInt(constraint.version) + 1;
        constraint.type = editType;
        constraint.priority = editPriority;
        constraint.volumes.forEach((vol) => {
            vol.altitude_max_agl_m = ConvertFeetToMeters(parseFloat(editAltitude));
        });
        const requestOptions = {
            method: "PUT",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify(constraint)
        };
        fetch("api/constraint/update", requestOptions)
            .then((response) => (response.ok ? response.json() : Promise.reject(response)))
            .then(() => {
                props.setSnackbar({ children: "Constraint successfully updated", severity: "success" });
                props.setConstraintEditDialogOpen(false);
            })
            .catch((err) => handleFailedFetch(err));
    };

    // Fires if delete button clicked
    const handleDeleteConstraint = () => {
        if (!confirm("Are you sure you want to delete this constraint?")) return;

        const constraint = props.constraintEditEntity.constraint;

        //Put Operations data over FETCH request
        const requestOptions = {
            method: "PUT",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify(constraint)
        };
        fetch("/api/constraint/delete", requestOptions)
            .then((response) => (response.ok ? response.json() : Promise.reject(response)))
            .then(() => {
                props.setSnackbar({ children: "Constraint successfully deleted", severity: "success" });
                props.setConstraintEditDialogOpen(false);
            })
            .catch((err) => handleFailedFetch(err));
    };
    const handleTypeChange = (e) => {
        const type = e.target.value;
        setEditType(type);

        if (type === RESTRICTED_TYPE) {
            setEditPriority(1000);
        } else if (type === EMERGENCY_TYPE) {
            setEditPriority(40);
        } else if (type === PUBLIC_SAFETY_TYPE) {
            setEditPriority(30);
        } else if (type === COMMERCIAL_TYPE) {
            setEditPriority(20);
        } else if (type === TRAINING_TYPE) {
            setEditPriority(10);
        } else if (type === ADVISORY_TYPE) {
            setEditPriority(0);
        }
    };
    const handleStartDateChange = (startDate) => {
        setEditStart(startDate);

        const durationMin = parseFloat(editDuration);
        const validDuration = !isNaN(durationMin) && durationMin > 0 && durationMin < YEAR_IN_MINUTES;

        if (dayjs(startDate).isValid() && validDuration) {
            setEditEnd(dayjs(startDate).add(durationMin, "minutes"));
        }
    };
    const handleDurationChange = (e) => {
        const durationMin = getConstraintDurationFromInput(e.target.value);
        if (durationMin !== undefined) {
            setEditDuration(durationMin);
        }

        const validDuration = !isNaN(durationMin) && durationMin > 0 && durationMin < YEAR_IN_MINUTES;
        if (validDuration && dayjs(editStart).isValid() === true) {
            setEditEnd(dayjs(editStart).add(durationMin, "minutes"));
        }
    };
    return (
        <Dialog
            open={props.constraintEditDialogOpen}
            onContextMenu={(e) => e.preventDefault()}
            fullWidth={true}
            maxWidth={"sm"}
            container={props.mapContainer ? props.mapContainer : undefined}
        >
            <DialogTitle>Edit Constraint</DialogTitle>
            <DialogContent>
                <Box sx={{ display: "flex", flexDirection: { xs: "column", md: "row" }, gap: 2, mb: 2, pt: 1 }}>
                    <TextField
                        inputProps={{ "data-testid": "editName" }}
                        onChange={(e) => setEditName(e.target.value)}
                        error={props.snackbar && !editName}
                        variant="outlined"
                        value={editName}
                        id="nameInput"
                        label="Name"
                        sx={{ flex: 1 }}
                    />

                    <TextField
                        InputProps={{ endAdornment: <InputAdornment position="end">ft</InputAdornment> }}
                        inputProps={{ "data-testid": "editAltitude" }}
                        onChange={(e) => setEditAltitude(e.target.value)}
                        error={props.snackbar && (isNaN(parseFloat(editAltitude)) || parseFloat(editAltitude) <= 0)}
                        variant="outlined"
                        id="editAltitude"
                        label="Altitude (AGL)"
                        value={editAltitude}
                        type="number"
                        onWheel={(e) => e.target.blur()}
                        sx={{ flex: 1 }}
                    />

                    <FormControl sx={{ flex: 1 }}>
                        <InputLabel id="select-type-label">Type</InputLabel>
                        <Select
                            value={editType}
                            id="selectType"
                            labelId="select-type-label"
                            label="Type"
                            onChange={handleTypeChange}
                            inputProps={{ "data-testid": "selectType" }}
                            MenuProps={{ container: props.mapContainer }}
                        >
                            <MenuItem value={ADVISORY_TYPE} id="advisory">
                                Advisory
                            </MenuItem>
                            <MenuItem value={TRAINING_TYPE} id="training">
                                Training
                            </MenuItem>
                            <MenuItem value={COMMERCIAL_TYPE} id="commercial">
                                Commercial
                            </MenuItem>
                            <MenuItem value={PUBLIC_SAFETY_TYPE} id="safety">
                                Public Safety
                            </MenuItem>
                            <MenuItem value={EMERGENCY_TYPE} id="emergency">
                                Emergency
                            </MenuItem>
                            <MenuItem value={RESTRICTED_TYPE} id="restricted">
                                Restricted
                            </MenuItem>
                        </Select>
                    </FormControl>
                </Box>

                <Box sx={{ display: "flex", flexDirection: { xs: "column", md: "row" }, gap: 2 }}>
                    <Box sx={{ flex: 1 }}>
                        <LocalizationProvider dateAdapter={AdapterDayjs}>
                            <DateTimePicker
                                label={`Start Time (${GetTimezone()}) *`}
                                value={dayjs(editStart)}
                                onChange={handleStartDateChange}
                                slotProps={{
                                    textField: {
                                        fullWidth: true,
                                        error: props.snackbar && !publishedConstraint && DateHasAlreadyPassed(editStart),
                                        inputProps: { "data-testid": "startTime" }
                                    },
                                    popper: {
                                        container: props.mapContainer
                                    }
                                }}
                            />
                        </LocalizationProvider>
                    </Box>

                    <TextField
                        error={
                            props.snackbar && (isNaN(parseFloat(editDuration)) || parseFloat(editDuration) <= 0 || parseFloat(editDuration) >= YEAR_IN_MINUTES)
                        }
                        inputProps={{ "data-testid": "editDuration" }}
                        InputProps={{ endAdornment: <InputAdornment position="end">mins</InputAdornment> }}
                        variant="outlined"
                        label="Duration"
                        onChange={handleDurationChange}
                        value={editDuration}
                        sx={{ flex: 1 }}
                    />
                </Box>
            </DialogContent>
            <DialogActions>
                <Button onClick={handleDeleteConstraint} data-testid="delete" size="small" sx={{ mr: "auto" }}>
                    Delete
                </Button>
                <Button onClick={() => props.setConstraintEditDialogOpen(false)} data-testid="cancel" size="small">
                    Cancel
                </Button>
                <Button onClick={handleUpdateConstraint} data-testid="accept" size="small">
                    Save
                </Button>
            </DialogActions>
        </Dialog>
    );
};
